import type { MergeTag, SyntaxPreset } from "@aswin.dev/types";
import {
  isMergeTagValue as checkIsMergeTagValue,
  getMergeTagLabel as resolveMergeTagLabel,
  SYNTAX_PRESETS,
} from "@aswin.dev/types";
import { inject, ref, type Ref } from "vue";
import {
  EDITOR_TYPE_KEY,
  MERGE_TAGS_KEY,
  MERGE_TAG_SYNTAX_KEY,
  MERGE_TAG_AUTOCOMPLETE_KEY,
  ON_REQUEST_MERGE_TAG_KEY,
} from "../keys";

export interface UseMergeTagReturn {
  /** Available merge tags from config */
  mergeTags: MergeTag[];
  /** Whether a merge tag request is in progress */
  isRequesting: Ref<boolean>;
  /**
   * Whether the "Insert merge tag" button should be shown. True only when
   * `onRequestMergeTag` is provided — static `tags` alone are surfaced via
   * the autocomplete typing trigger, not the button.
   */
  canRequestMergeTag: boolean;
  /** Whether typing-based autocomplete is enabled by configuration */
  autocomplete: boolean;
  /** The resolved syntax preset for merge tags */
  syntax: SyntaxPreset;
  /** Check if a value matches the configured merge tag syntax */
  isMergeTagValue: (value: string) => boolean;
  /** Get the human-readable label for a merge tag value */
  getMergeTagLabel: (value: string) => string;
  /** Request a merge tag from the user via the configured callback */
  requestMergeTag: () => Promise<MergeTag | null>;
}

/**
 * Composable for merge tag functionality.
 * Provides utilities for detecting, displaying, and requesting merge tags.
 */
export function useMergeTag(): UseMergeTagReturn {
  const editorType = inject(EDITOR_TYPE_KEY, "email");
  const isPopup = editorType === "popup";

  const mergeTags = isPopup ? [] : inject(MERGE_TAGS_KEY, []);
  const syntax = inject(MERGE_TAG_SYNTAX_KEY, SYNTAX_PRESETS.liquid);
  const onRequestMergeTag = isPopup
    ? null
    : inject(ON_REQUEST_MERGE_TAG_KEY, null);
  const autocomplete = isPopup
    ? false
    : inject(MERGE_TAG_AUTOCOMPLETE_KEY, true);

  const isRequesting = ref(false);

  function isMergeTagValue(value: string): boolean {
    return checkIsMergeTagValue(value, syntax);
  }

  function getMergeTagLabel(value: string): string {
    return resolveMergeTagLabel(value, mergeTags);
  }

  /**
   * Request a merge tag from the user via the configured callback.
   * Returns null if no callback is configured or user cancels.
   */
  async function requestMergeTag(): Promise<MergeTag | null> {
    if (!onRequestMergeTag) {
      return null;
    }

    isRequesting.value = true;
    try {
      return await onRequestMergeTag();
    } finally {
      isRequesting.value = false;
    }
  }

  const canRequestMergeTag = !!onRequestMergeTag;

  return {
    mergeTags,
    isRequesting,
    canRequestMergeTag,
    autocomplete,
    syntax,
    isMergeTagValue,
    getMergeTagLabel,
    requestMergeTag,
  };
}
